import { translateUi } from "@/shared/lib/ui-translate";
import { z } from "zod";

import { post } from "@/shared/lib/http/zod-http";
import { deleteTeamsId } from "@/shared/api/generated-kubb/hooks/useDeleteTeamsId";
import { deleteTeamsIdMembersMemberid } from "@/shared/api/generated-kubb/hooks/useDeleteTeamsIdMembersMemberid";
import { deleteTeamsInvitationsInvitationid } from "@/shared/api/generated-kubb/hooks/useDeleteTeamsInvitationsInvitationid";
import { getTeams } from "@/shared/api/generated-kubb/hooks/useGetTeams";
import { getTeamsId } from "@/shared/api/generated-kubb/hooks/useGetTeamsId";
import { getTeamsIdInviteToken } from "@/shared/api/generated-kubb/hooks/useGetTeamsIdInviteToken";
import { getTeamsTeamidInvitations } from "@/shared/api/generated-kubb/hooks/useGetTeamsTeamidInvitations";
import { getTeamsInviteToken } from "@/shared/api/generated-kubb/hooks/useGetTeamsInviteToken";
import { getTeamsUserTeamInvitations } from "@/shared/api/generated-kubb/hooks/useGetTeamsUserTeamInvitations";
import { getTeamsUserTeamInvitationsTeamid } from "@/shared/api/generated-kubb/hooks/useGetTeamsUserTeamInvitationsTeamid";
import { patchTeamsId } from "@/shared/api/generated-kubb/hooks/usePatchTeamsId";
import { patchTeamsIdCaptain } from "@/shared/api/generated-kubb/hooks/usePatchTeamsIdCaptain";
import { postTeamsIdInviteRegenerate } from "@/shared/api/generated-kubb/hooks/usePostTeamsIdInviteRegenerate";
import { postTeamsIdLeave } from "@/shared/api/generated-kubb/hooks/usePostTeamsIdLeave";
import { postTeamsIdMembers } from "@/shared/api/generated-kubb/hooks/usePostTeamsIdMembers";
import { postTeamsInvitationsInvitationidAccept } from "@/shared/api/generated-kubb/hooks/usePostTeamsInvitationsInvitationidAccept";
import { postTeamsInvitationsInvitationidDecline } from "@/shared/api/generated-kubb/hooks/usePostTeamsInvitationsInvitationidDecline";
import { postTeamsInviteToken } from "@/shared/api/generated-kubb/hooks/usePostTeamsInviteToken";
import { postTeamsTeamidInvite } from "@/shared/api/generated-kubb/hooks/usePostTeamsTeamidInvite";
import { putTeamsIdMembersMemberidRole } from "@/shared/api/generated-kubb/hooks/usePutTeamsIdMembersMemberidRole";
import { handleSilentError, extractApiErrorMessage } from "@/shared/api/error-handler";
import { api, type ResponseConfig } from "@/shared/lib/api";
import { getTeams200Schema } from "@/shared/api/generated-kubb/schemas/getTeamsSchema";
import { getTeamsId200Schema } from "@/shared/api/generated-kubb/schemas/getTeamsIdSchema";
import { getTeamsIdInviteToken200Schema } from "@/shared/api/generated-kubb/schemas/getTeamsIdInviteTokenSchema";
import { getTeamsInviteToken200Schema } from "@/shared/api/generated-kubb/schemas/getTeamsInviteTokenSchema";
import { postTeamsIdInviteRegenerate200Schema } from "@/shared/api/generated-kubb/schemas/postTeamsIdInviteRegenerateSchema";
import { postTeamsInviteToken200Schema } from "@/shared/api/generated-kubb/schemas/postTeamsInviteTokenSchema";

import { CreateTeamResponseSchema } from "./response-types.schemas";
import {
  CreateRosterRequestSchema,
  UpdateRosterRequestSchema,
  RosterInviteRequestSchema,
} from "./roster.schemas";
import { parseRequestLoose } from "./parse-request-loose";
import { goodLuckCoreDTOsRosterCurrentUserContextSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/DTOs/rosterCurrentUserContextSchema";
import { goodLuckCoreDTOsRosterMemberResponseSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/DTOs/rosterMemberResponseSchema";
import { goodLuckCoreDTOsRosterResponseSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/DTOs/rosterResponseSchema";
import { goodLuckCoreEntitiesRosterInvitationSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/entities/rosterInvitationSchema";
import { goodLuckCoreEntitiesRosterMemberSchema } from "@/shared/api/generated-kubb/schemas/goodLuck/core/entities/rosterMemberSchema";

// Utility: strip index signature from Zod-inferred types so
// noPropertyAccessFromIndexSignature doesn't force bracket notation everywhere.
type StripIndex<T> = {
  [K in keyof T as string extends K ? never : K]: T[K];
};

// KAN-200: типизированные помощники вместо any при разборе сырых ответов бэка.
type AxiosLikeError = {
  response?: { status?: number; data?: { message?: string } };
  message?: string;
};

const asStr = (v: unknown): string | undefined =>
  typeof v === "string" && v !== "" ? v : undefined;

// Сырое приглашение с бэка: ключи могут прийти в camelCase и PascalCase,
// значения не доверяем до нормализации.
type RawTeamInvitation = {
  id?: unknown; Id?: unknown;
  teamId?: unknown; TeamId?: unknown;
  invitedUserId?: unknown; InvitedUserId?: unknown;
  inviteeId?: unknown; InviteeId?: unknown;
  userId?: unknown; UserId?: unknown;
  invitedByUserId?: unknown; InvitedByUserId?: unknown;
  inviterId?: unknown; InviterId?: unknown;
  createdByUserId?: unknown; CreatedByUserId?: unknown;
  status?: unknown; Status?: unknown;
  role?: unknown; Role?: unknown;
  createdAt?: unknown; CreatedAt?: unknown;
  respondedAt?: unknown; RespondedAt?: unknown;
  expiresAt?: unknown; ExpiresAt?: unknown;
  team?: unknown; Team?: unknown;
  invitedUser?: unknown; InvitedUser?: unknown;
  invitedByUser?: unknown; InvitedByUser?: unknown;
  teamName?: unknown; TeamName?: unknown;
  inviterName?: unknown; InviterName?: unknown;
  invitedUserName?: unknown; InvitedUserName?: unknown;
  message?: unknown; Message?: unknown;
};

// Types - используем сгенерированные схемы из Kubb
export type RosterMember = StripIndex<
  z.infer<typeof goodLuckCoreDTOsRosterMemberResponseSchema>
> & {
  name?: string | null;
  avatar?: string | null;
  isOnline?: boolean;
  rank?: string | null;
  region?: string | null;
  isCaptain?: boolean;
  riotProfileIconId?: number | null;
  riotRank?: string | null;
  riotLane?: string | null;
  hasPro?: boolean;
  proNicknameStyle?: number;
  proNicknameColorA?: string | null;
  proNicknameColorB?: string | null;
};
export type RosterCurrentUserContext = StripIndex<
  z.infer<typeof goodLuckCoreDTOsRosterCurrentUserContextSchema>
>;
export type Team = Omit<
  StripIndex<z.infer<typeof goodLuckCoreDTOsRosterResponseSchema>>,
  "position" | "prize" | "members" | "tournaments"
> & {
  game?: string | null;
  coach?: string | null;
  tournament?: string | null;
  captain?: string | null;
  position?: string | number | null;
  prize?: string | number | null;
  acceptScrimInvites?: boolean;
  isVerified?: boolean;
  members?: RosterMember[] | null;
  tournaments?: Array<{
    name?: string | null;
    position?: string | number | null;
    prize?: string | number | null;
    year?: string | null;
  }> | null;
};

export type CreateRosterRequest = z.infer<typeof CreateRosterRequestSchema>;

export type UpdateRosterRequest = z.infer<typeof UpdateRosterRequestSchema>;

export type InviteRosterMemberRequest = z.infer<typeof RosterInviteRequestSchema>;

function mergeTeamMembersFromRaw<T extends { members?: RosterMember[] | null }>(
  team: T,
  raw: unknown,
): T {
  const members = team.members;
  if (!members?.length) return team;
  const rawObj = raw as { Members?: unknown[]; members?: unknown[] };
  const rawMembers = rawObj.members ?? rawObj.Members;
  if (!Array.isArray(rawMembers)) return team;
  return {
    ...team,
    members: members.map((m, i) => {
      const x = rawMembers[i] as Record<string, unknown> | undefined;
      if (!x) return m;
      const iconId = x["riotProfileIconId"] ?? x["RiotProfileIconId"];
      const rank = x["riotRank"] ?? x["RiotRank"];
      const lane = x["riotLane"] ?? x["RiotLane"];
      const hasPro = x["hasPro"] ?? x["HasPro"];
      const proNicknameStyle = x["proNicknameStyle"] ?? x["ProNicknameStyle"];
      const proNicknameColorA = x["proNicknameColorA"] ?? x["ProNicknameColorA"];
      const proNicknameColorB = x["proNicknameColorB"] ?? x["ProNicknameColorB"];
      return {
        ...m,
        ...(typeof iconId === "number"
          ? { riotProfileIconId: iconId }
          : typeof iconId === "string" && iconId !== ""
            ? { riotProfileIconId: Number(iconId) || undefined }
            : {}),
        ...(typeof rank === "string" && rank ? { riotRank: rank } : {}),
        ...(typeof lane === "string" && lane ? { riotLane: lane } : {}),
        ...(hasPro === true || hasPro === false ? { hasPro: Boolean(hasPro) } : {}),
        ...(typeof proNicknameStyle === "number"
          ? { proNicknameStyle }
          : typeof proNicknameStyle === "string" && proNicknameStyle !== ""
            ? { proNicknameStyle: Number(proNicknameStyle) || 0 }
            : {}),
        ...(typeof proNicknameColorA === "string"
          ? { proNicknameColorA }
          : {}),
        ...(typeof proNicknameColorB === "string"
          ? { proNicknameColorB }
          : {}),
      };
    }),
  };
}

type TeamInvitationUser = {
  id?: string | null;
  username?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
};

export type RosterInvitation = {
  id?: string | null;
  teamId?: string | null;
  invitedUserId?: string;
  invitedByUserId?: string;
  status?: "Pending" | "Accepted" | "Rejected" | "Expired" | "Cancelled";
  role?: "Top" | "Jungle" | "Mid" | "Adc" | "Support" | "Substitute" | "Coach";
  createdAt?: string;
  respondedAt?: string | null;
  expiresAt?: string;
  invitedUser?: TeamInvitationUser;
  invitedByUser?: TeamInvitationUser;
  teamName?: string | null;
  inviterName?: string | null;
  invitedUserName?: string | null;
  message?: string | null;
};

const TeamInviteResponseLiteSchema = z.object({
  success: z.boolean().optional(),
  invitation: z.unknown().optional(),
  message: z.string().max(200).optional(),
});

// API functions
export const rosterApi = {
  getTeams: async (params?: {
    game?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<Team[]> => {
    const searchParams = new URLSearchParams();
    if (params?.game) searchParams.set("game", params.game);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.limit) searchParams.set("limit", params.limit.toString());

    // Сген. getTeams теперь принимает query-params напрямую (раньше {params}).
    const response = (await getTeams(
      Object.fromEntries(searchParams) as unknown as Parameters<typeof getTeams>[0],
    )) as unknown as ResponseConfig<unknown>;

    // response.data уже нормализован в Axios interceptor.
    // Ответ стал пагинированным ({ items }) — возвращаем сам список.
    const validated = getTeams200Schema.parse(response.data);
    return (validated.items ?? []) as unknown as Team[];
  },

  getTeamById: async (teamIdOrSlug: string): Promise<Team> => {
    // Используем сгенерированную функцию вместо TeamsService
    // API поддерживает как UUID, так и slug
    const response = (await getTeamsId(
      teamIdOrSlug,
    )) as unknown as ResponseConfig<unknown>;
    // response.data уже нормализован в Axios interceptor
    const validated = getTeamsId200Schema.parse(response.data);
    return mergeTeamMembersFromRaw(validated, response.data);
  },

  createTeam: async (data: CreateRosterRequest): Promise<Team> => {
    // Валидация входящих данных
    const validatedData = parseRequestLoose(CreateRosterRequestSchema, data);

    const parsed = await post(
      "/rosters/create",
      validatedData,
      CreateTeamResponseSchema,
    );

    // Бэкенд возвращает { id: string, success: boolean }
    // Проверяем структуру ответа
    if (!parsed || !parsed.id) {
      console.error("[rosterApi] createTeam: неожиданный формат ответа:", parsed);
      throw new Error(translateUi("litRoster.neozhidannyyFormatOtveta"));
    }

    // Получаем команду по ID
    if (parsed.id) {
      // Используем сгенерированную функцию напрямую
      const response = (await getTeamsId(
        parsed.id,
      )) as unknown as ResponseConfig<unknown>;
      const validated = getTeamsId200Schema.parse(response.data);
      return mergeTeamMembersFromRaw(validated, response.data);
    }

    throw new Error(translateUi("litRoster.neUdalosPoluchitId"));
  },

  updateTeam: async (
    teamId: string,
    data: UpdateRosterRequest,
  ): Promise<Team> => {
    // Валидация входящих данных
    const validatedData = parseRequestLoose(UpdateRosterRequestSchema, data);

    await patchTeamsId(teamId, validatedData);

    const response = (await getTeamsId(teamId)) as unknown as ResponseConfig<unknown>;
    const validated = getTeamsId200Schema.parse(response.data);
    return mergeTeamMembersFromRaw(validated, response.data);
  },

  deleteTeam: async (teamId: string): Promise<void> => {
    await deleteTeamsId(teamId);
  },

  getTeamMembers: async (teamId: string): Promise<RosterMember[]> => {
    const response = (await getTeamsId(teamId)) as unknown as ResponseConfig<unknown>;
    const validated = getTeamsId200Schema.parse(response.data);
    const merged = mergeTeamMembersFromRaw(validated, response.data);
    const members = Array.isArray(merged?.members) ? merged.members : [];
    return members as RosterMember[];
  },

  // Add team member
  addTeamMember: async (
    teamId: string,
    data: {
      userId: string;
      role: string;
    },
  ): Promise<RosterMember> => {
    const response = (await postTeamsIdMembers(
      teamId,
      data as Parameters<typeof postTeamsIdMembers>[1],
    )) as unknown as ResponseConfig<unknown>;
    const rosterMemberParser = goodLuckCoreEntitiesRosterMemberSchema as unknown as {
      parse: (v: unknown) => unknown;
    };
    const member = rosterMemberParser.parse(response.data);
    return member as unknown as RosterMember;
  },

  removeTeamMember: async (teamId: string, memberId: string): Promise<void> => {
    await deleteTeamsIdMembersMemberid(teamId, memberId);
  },

  updateMemberRole: async (
    teamId: string,
    memberId: string,
    role: string,
  ): Promise<RosterMember> => {
    const response = (await putTeamsIdMembersMemberidRole(
      teamId,
      memberId,
      { role } as Parameters<typeof putTeamsIdMembersMemberidRole>[2],
    )) as unknown as ResponseConfig<unknown>;
    const rosterMemberParser = goodLuckCoreEntitiesRosterMemberSchema as unknown as {
      parse: (v: unknown) => unknown;
    };
    const member = rosterMemberParser.parse(response.data);
    return member as unknown as RosterMember;
  },

  updateTeamCaptain: async (
    teamId: string,
    newCaptainId: string,
  ): Promise<void> => {
    await patchTeamsIdCaptain(
      teamId,
      { newCaptainId } as Parameters<typeof patchTeamsIdCaptain>[1],
    );
  },

  // Invite team member
  inviteTeamMember: async (
    teamId: string,
    data: InviteRosterMemberRequest,
  ): Promise<RosterInvitation> => {
    // Валидация входящих данных
    const validatedData = parseRequestLoose(RosterInviteRequestSchema, data);

    try {
      const response = (await postTeamsTeamidInvite(
        teamId,
        validatedData as Parameters<typeof postTeamsTeamidInvite>[1],
      )) as unknown as ResponseConfig<unknown>;

      // Валидация исходящих данных - backend может вернуть success + invitation/id
      const parsed = TeamInviteResponseLiteSchema.safeParse(response.data);
      if (!parsed.success) {
        // Если структура ответа отличается, просто проверяем success
        if ((response.data as { success?: boolean } | null)?.success === true) {
          // Возвращаем пустой объект приглашения, так как полное приглашение не возвращается
          return {} as RosterInvitation;
        }
        throw parsed.error;
      }
      if (parsed.data?.success === true) {
        return (parsed.data.invitation as RosterInvitation) || ({} as RosterInvitation);
      }
      return (parsed.data.invitation as RosterInvitation) || ({} as RosterInvitation);
    } catch (error: unknown) {
      const err = error as AxiosLikeError;
      // Улучшенная обработка ошибок для дублирующихся приглашений
      if (err?.response?.status === 400) {
        const errorMessage =
          extractApiErrorMessage(err, "");

        // Проверяем, содержит ли сообщение информацию о существующем приглашении
        if (
          errorMessage.includes("уже отправлено") ||
          errorMessage.includes("ID:")
        ) {
          // Извлекаем ID приглашения из сообщения, если есть
          const invitationIdMatch = errorMessage.match(/ID:\s*([^\s)]+)/);
          const invitationId = invitationIdMatch ? invitationIdMatch[1] : null;

          // Создаем более информативную ошибку
          const enhancedError = new Error(
            invitationId ? translateUi("litRoster.priglashenieUzheOtpravlenoId", { id: invitationId }) : translateUi("litRoster.priglashenieUzheOtpravleno"),
          );
          Object.assign(enhancedError, {
            invitationId,
            isDuplicateInvitation: true,
          });
          throw enhancedError;
        }
      }
      throw error;
    }
  },

  getUserTeamInvitationForTeam: async (
    teamId: string,
  ): Promise<RosterInvitation | null> => {
    try {
      const response =
        (await getTeamsUserTeamInvitationsTeamid(
          teamId,
        )) as unknown as ResponseConfig<unknown>;
      if (!response.data) {
        return null;
      }

      // Адаптируем данные от бэкенда к формату фронтенда
      const x = (response.data ?? {}) as RawTeamInvitation;
      const normalizeStatus = (
        status: unknown,
      ): "pending" | "accepted" | "declined" | "expired" => {
        const statusStr = String(status ?? "Pending").trim();
        const lowerStatus = statusStr.toLowerCase();

        if (lowerStatus === "rejected" || lowerStatus === "cancelled") {
          return "declined";
        }
        if (lowerStatus === "pending") {
          return "pending";
        }
        if (lowerStatus === "accepted") {
          return "accepted";
        }
        if (lowerStatus === "expired") {
          return "expired";
        }
        return "pending";
      };

      const normalizeDateTime = (dateValue: unknown): string | undefined => {
        if (!dateValue) return undefined;

        try {
          let date: Date;

          if (typeof dateValue === "string") {
            if (
              dateValue.match(
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/,
              )
            ) {
              return dateValue;
            }
            if (
              dateValue.match(
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?[+-]\d{2}:\d{2}$/,
              )
            ) {
              date = new Date(dateValue);
            } else {
              date = new Date(dateValue);
            }
          } else {
            date = new Date(dateValue as number | Date);
          }

          if (isNaN(date.getTime())) {
            return undefined;
          }

          return date.toISOString();
        } catch (_e) {
          return undefined;
        }
      };

      const id = asStr(x.id) ?? asStr(x.Id);
      if (!id) {
        return null;
      }

      const adapted = {
        id: id,
        teamId: asStr(x.teamId) ?? asStr(x.TeamId) ?? "",
        teamName: asStr(x.teamName) ?? asStr(x.TeamName) ?? "",
        inviterName:
          asStr(x.inviterName) ??
          asStr(x.InviterName) ??
          "Неизвестный пользователь",
        invitedUserName: asStr(x.invitedUserName) ?? asStr(x.InvitedUserName),
        role: asStr(x.role) ?? asStr(x.Role) ?? "Substitute",
        status: normalizeStatus(x.status ?? x.Status),
        createdAt: normalizeDateTime(x.createdAt ?? x.CreatedAt),
        expiresAt: normalizeDateTime(x.expiresAt ?? x.ExpiresAt),
        message: asStr(x.message) ?? asStr(x.Message),
      };

      return adapted as unknown as RosterInvitation;
    } catch (error) {
      handleSilentError(error, {
        component: "rosterApi",
        action: "getUserTeamInvitationForTeam",
      });
      return null;
    }
  },

  getTeamInvitations: async (teamId: string): Promise<RosterInvitation[]> => {
    const response =
      (await getTeamsTeamidInvitations(teamId)) as unknown as ResponseConfig<unknown>;
    const arr = Array.isArray(response.data) ? response.data : [];

    // Функция для преобразования статуса
    const normalizeStatus = (
      status: unknown,
    ): "Pending" | "Accepted" | "Rejected" | "Expired" | "Cancelled" => {
      const raw = String(status ?? "Pending").trim();
      if (
        raw === "Pending" ||
        raw === "Accepted" ||
        raw === "Rejected" ||
        raw === "Expired" ||
        raw === "Cancelled"
      ) {
        return raw;
      }

      const lower = raw.toLowerCase();

      if (lower === "accepted") return "Accepted";
      if (lower === "expired") return "Expired";
      if (lower === "cancelled" || lower === "canceled") return "Cancelled";
      if (lower === "rejected" || lower === "declined") return "Rejected";
      if (lower === "pending") return "Pending";

      return "Pending";
    };

    const normalizeRole = (
      role: unknown,
    ): "Top" | "Jungle" | "Mid" | "Adc" | "Support" | "Substitute" | "Coach" => {
      const raw = String(role ?? "Substitute").trim();
      if (
        raw === "Top" ||
        raw === "Jungle" ||
        raw === "Mid" ||
        raw === "Adc" ||
        raw === "Support" ||
        raw === "Substitute" ||
        raw === "Coach"
      ) {
        return raw;
      }

      const lower = raw.toLowerCase();
      if (lower === "adc") return "Adc";
      if (lower === "top") return "Top";
      if (lower === "jungle") return "Jungle";
      if (lower === "mid") return "Mid";
      if (lower === "support") return "Support";
      if (lower === "coach" || lower === "тренер") return "Coach";
      if (lower === "substitute" || lower === "запасной") return "Substitute";

      return "Substitute";
    };

    // Функция для преобразования даты в ISO формат (требуется для Zod datetime)
    const normalizeDateTime = (dateValue: unknown): string | undefined => {
      if (!dateValue) return undefined;

      try {
        let date: Date;

        // Если это уже строка
        if (typeof dateValue === "string") {
          // Проверяем, является ли строка уже валидным ISO форматом с Z
          if (
            dateValue.match(
              /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/,
            )
          ) {
            return dateValue;
          }
          // Проверяем формат с timezone offset
          if (
            dateValue.match(
              /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?[+-]\d{2}:\d{2}$/,
            )
          ) {
            // Конвертируем в UTC и возвращаем с Z
            date = new Date(dateValue);
          } else {
            // Пытаемся распарсить как обычную дату
            date = new Date(dateValue);
          }
        } else {
          // Если это объект Date или число (timestamp)
          date = new Date(dateValue as number | Date);
        }

        // Проверяем, что дата валидна
        if (isNaN(date.getTime())) {
          console.warn("[getTeamInvitations] Некорректная дата:", dateValue);
          return undefined;
        }

        // Возвращаем в ISO формате с Z (UTC)
        return date.toISOString();
      } catch (e) {
        console.warn(
          "[getTeamInvitations] Ошибка при преобразовании даты:",
          dateValue,
          e,
        );
        return undefined;
      }
    };

    return arr
      .map((rawItem: unknown) => {
        const x = rawItem as RawTeamInvitation;
        // Получаем id - проверяем оба варианта (camelCase и PascalCase)
        const id = x.id || x.Id;
        if (!id || id === "") {
          console.warn(
            "[getTeamInvitations] Пропущено приглашение с пустым ID:",
            x,
          );
          return null;
        }

        const invitedUserId = String(
          x.invitedUserId ??
          x.InvitedUserId ??
          x.inviteeId ??
          x.InviteeId ??
          x.userId ??
          x.UserId ??
          "",
        ).trim();
        const invitedByUserId = String(
          x.invitedByUserId ??
          x.InvitedByUserId ??
          x.inviterId ??
          x.InviterId ??
          x.createdByUserId ??
          x.CreatedByUserId ??
          "",
        ).trim();

        if (!invitedUserId || !invitedByUserId) {
          console.warn(
            "[getTeamInvitations] Пропущено приглашение без invitedUserId/invitedByUserId:",
            x,
          );
          return null;
        }

        // Адаптируем данные от бэкенда к core-схеме (Kubb)
        const adaptedCore = {
          id: String(id),
          teamId: String(x.teamId ?? x.TeamId ?? teamId ?? "").trim() || null,
          invitedUserId,
          invitedByUserId,
          status: normalizeStatus(x.status ?? x.Status),
          role: normalizeRole(x.role ?? x.Role),
          createdAt: normalizeDateTime(x.createdAt ?? x.CreatedAt),
          respondedAt: normalizeDateTime(x.respondedAt ?? x.RespondedAt) ?? null,
          team: x.team ?? x.Team,
          invitedUser: x.invitedUser ?? x.InvitedUser,
          invitedByUser: x.invitedByUser ?? x.InvitedByUser,
        };

        const rosterInvitationParser =
          goodLuckCoreEntitiesRosterInvitationSchema as unknown as {
            parse: (v: unknown) => Record<string, unknown>;
          };
        const parsed = rosterInvitationParser.parse(adaptedCore);

        const invitedByUser = parsed["invitedByUser"] as
          | { displayName?: string | null; username?: string | null }
          | undefined;
        const invitedUser = parsed["invitedUser"] as
          | { username?: string | null }
          | undefined;

        return {
          ...parsed,
          teamName:
            (parsed["team"] as { name?: string } | undefined)?.name ??
            asStr(x.teamName) ??
            asStr(x.TeamName) ??
            null,
          inviterName:
            invitedByUser?.displayName ??
            invitedByUser?.username ??
            asStr(x.inviterName) ??
            asStr(x.InviterName) ??
            null,
          invitedUserName:
            invitedUser?.username ??
            asStr(x.invitedUserName) ??
            asStr(x.InvitedUserName) ??
            null,
          message: asStr(x.message) ?? asStr(x.Message) ?? null,
        } satisfies RosterInvitation;
      })
      .filter((item) => item != null) as RosterInvitation[];
  },

  // Accept team invitation
  acceptTeamInvitation: async (invitationId: string): Promise<void> => {
    await postTeamsInvitationsInvitationidAccept(invitationId);
  },

  // Decline team invitation
  declineTeamInvitation: async (invitationId: string): Promise<void> => {
    await postTeamsInvitationsInvitationidDecline(invitationId);
  },

  // Cancel team invitation (by captain)
  cancelTeamInvitation: async (invitationId: string): Promise<void> => {
    await deleteTeamsInvitationsInvitationid(invitationId);
  },

  getUserTeamInvitations: async (): Promise<RosterInvitation[]> => {
    try {
      const response =
        (await getTeamsUserTeamInvitations()) as unknown as ResponseConfig<unknown>;
      const arr = Array.isArray(response.data) ? response.data : [];

      // Функция для преобразования статуса
      const normalizeStatus = (
        status: unknown,
      ): "Pending" | "Accepted" | "Rejected" | "Expired" | "Cancelled" => {
        const raw = String(status ?? "Pending").trim();
        if (
          raw === "Pending" ||
          raw === "Accepted" ||
          raw === "Rejected" ||
          raw === "Expired" ||
          raw === "Cancelled"
        ) {
          return raw;
        }

        const lower = raw.toLowerCase();
        if (lower === "accepted") return "Accepted";
        if (lower === "expired") return "Expired";
        if (lower === "cancelled" || lower === "canceled") return "Cancelled";
        if (lower === "rejected" || lower === "declined") return "Rejected";
        if (lower === "pending") return "Pending";
        return "Pending";
      };

      const normalizeRole = (
        role: unknown,
      ): "Top" | "Jungle" | "Mid" | "Adc" | "Support" | "Substitute" | "Coach" => {
        const raw = String(role ?? "Substitute").trim();
        if (
          raw === "Top" ||
          raw === "Jungle" ||
          raw === "Mid" ||
          raw === "Adc" ||
          raw === "Support" ||
          raw === "Substitute" ||
          raw === "Coach"
        ) {
          return raw;
        }

        const lower = raw.toLowerCase();
        if (lower === "adc") return "Adc";
        if (lower === "top") return "Top";
        if (lower === "jungle") return "Jungle";
        if (lower === "mid") return "Mid";
        if (lower === "support") return "Support";
        if (lower === "coach" || lower === "тренер") return "Coach";
        if (lower === "substitute" || lower === "запасной") return "Substitute";

        return "Substitute";
      };

      // Функция для преобразования даты в ISO формат (требуется для Zod datetime)
      const normalizeDateTime = (dateValue: unknown): string | undefined => {
        if (!dateValue) return undefined;

        try {
          let date: Date;

          // Если это уже строка
          if (typeof dateValue === "string") {
            // Проверяем, является ли строка уже валидным ISO форматом с Z
            if (
              dateValue.match(
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/,
              )
            ) {
              return dateValue;
            }
            // Проверяем формат с timezone offset
            if (
              dateValue.match(
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?[+-]\d{2}:\d{2}$/,
              )
            ) {
              // Конвертируем в UTC и возвращаем с Z
              date = new Date(dateValue);
            } else {
              // Пытаемся распарсить как обычную дату
              date = new Date(dateValue);
            }
          } else {
            // Если это объект Date или число (timestamp)
            date = new Date(dateValue as number | Date);
          }

          // Проверяем, что дата валидна
          if (isNaN(date.getTime())) {
            console.warn(
              "[getUserTeamInvitations] Некорректная дата:",
              dateValue,
            );
            return undefined;
          }

          // Возвращаем в ISO формате с Z (UTC)
          return date.toISOString();
        } catch (e) {
          console.warn(
            "[getUserTeamInvitations] Ошибка при преобразовании даты:",
            dateValue,
            e,
          );
          return undefined;
        }
      };

      return arr
        .map((rawItem: unknown) => {
          const x = rawItem as RawTeamInvitation;
          // Получаем id - проверяем оба варианта (camelCase и PascalCase)
          const id = x.id || x.Id;
          if (!id || id === "") {
            console.warn(
              "[getUserTeamInvitations] Пропущено приглашение с пустым ID:",
              x,
            );
            return null;
          }

          // Адаптируем данные от бэкенда к формату фронтенда
          const invitedUserId = String(
            x.invitedUserId ??
            x.InvitedUserId ??
            x.inviteeId ??
            x.InviteeId ??
            x.userId ??
            x.UserId ??
            "",
          ).trim();
          const invitedByUserId = String(
            x.invitedByUserId ??
            x.InvitedByUserId ??
            x.inviterId ??
            x.InviterId ??
            x.createdByUserId ??
            x.CreatedByUserId ??
            "",
          ).trim();

          if (!invitedUserId || !invitedByUserId) return null;

          const adaptedCore = {
            id: String(id),
            teamId: String(x.teamId ?? x.TeamId ?? "").trim() || null,
            invitedUserId,
            invitedByUserId,
            status: normalizeStatus(x.status ?? x.Status),
            role: normalizeRole(x.role ?? x.Role),
            createdAt: normalizeDateTime(x.createdAt ?? x.CreatedAt),
            respondedAt:
              normalizeDateTime(x.respondedAt ?? x.RespondedAt) ?? null,
            team: x.team ?? x.Team,
            invitedUser: x.invitedUser ?? x.InvitedUser,
            invitedByUser: x.invitedByUser ?? x.InvitedByUser,
          };

          const rosterInvitationParserU =
            goodLuckCoreEntitiesRosterInvitationSchema as unknown as {
              parse: (v: unknown) => Record<string, unknown>;
            };
          const parsed = rosterInvitationParserU.parse(adaptedCore);

          const invitedByUserU = parsed["invitedByUser"] as
            | { displayName?: string | null; username?: string | null }
            | undefined;
          const invitedUserU = parsed["invitedUser"] as
            | { username?: string | null }
            | undefined;

          return {
            ...parsed,
            teamName:
              (parsed["team"] as { name?: string } | undefined)?.name ??
              asStr(x.teamName) ??
              asStr(x.TeamName) ??
              null,
            inviterName:
              invitedByUserU?.displayName ??
              invitedByUserU?.username ??
              asStr(x.inviterName) ??
              asStr(x.InviterName) ??
              null,
            invitedUserName:
              invitedUserU?.username ??
              asStr(x.invitedUserName) ??
              asStr(x.InvitedUserName) ??
              null,
            message: asStr(x.message) ?? asStr(x.Message) ?? null,
          } satisfies RosterInvitation;
        })
        .filter((item) => item != null) as RosterInvitation[];
    } catch (error) {
      console.error(
        "[getUserTeamInvitations] Ошибка при получении приглашений:",
        error,
      );
      throw error;
    }
  },

  // Leave team
  leaveTeam: async (teamId: string): Promise<void> => {
    await postTeamsIdLeave(teamId);
  },

  // Regenerate invitation token
  regenerateInvitationToken: async (teamId: string): Promise<string> => {
    const response = (await postTeamsIdInviteRegenerate(
      teamId,
    )) as unknown as ResponseConfig<unknown>;
    const parsed = postTeamsIdInviteRegenerate200Schema.parse(response.data);
    return parsed.token ?? "";
  },

  // Get invitation token (creates if missing, but doesn't rotate if exists)
  getInvitationToken: async (teamId: string): Promise<string> => {
    const response = (await getTeamsIdInviteToken(
      teamId,
    )) as unknown as ResponseConfig<unknown>;
    const parsed = getTeamsIdInviteToken200Schema.parse(response.data);
    return parsed.token ?? "";
  },

  // Join team by token
  joinTeamByToken: async (token: string): Promise<string> => {
    const response = (await postTeamsInviteToken(
      token,
    )) as unknown as ResponseConfig<unknown>;
    const parsed = postTeamsInviteToken200Schema.parse(response.data);
    return parsed.teamId ?? "";
  },

  getTeamByToken: async (token: string): Promise<Team> => {
    const response = (await getTeamsInviteToken(
      token,
    )) as unknown as ResponseConfig<unknown>;
    const validated = getTeamsInviteToken200Schema.parse(response.data);
    return validated;
  },

  getScrimSchedule: async (teamId: string): Promise<ScrimScheduleSlot[]> => {
    const { data } = await api.get<ScrimScheduleSlot[]>(
      `/rosters/${encodeURIComponent(teamId)}/scrim-schedule`,
    );
    return Array.isArray(data) ? data : [];
  },

  setScrimSchedule: async (
    teamId: string,
    slots: ScrimScheduleSlot[],
  ): Promise<void> => {
    await api.put(`/rosters/${encodeURIComponent(teamId)}/scrim-schedule`, {
      slots,
    });
  },

  // ---- Public rosters listing (D1) ----

  getPublicRostersList: async (params?: {
    page?: number;
    pageSize?: number;
    isLookingForPlayers?: boolean;
  }): Promise<RosterListResponse> => {
    const { data } = await api.get<RosterListResponse>("/rosters", {
      params: {
        ...(params?.page !== undefined ? { page: params.page } : {}),
        ...(params?.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
        ...(params?.isLookingForPlayers !== undefined
          ? { isLookingForPlayers: params.isLookingForPlayers }
          : {}),
      },
    });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number(data?.total ?? 0),
      page: Number(data?.page ?? 1),
      pageSize: Number(data?.pageSize ?? 20),
    };
  },

  getTeamScrims: async (
    teamId: string,
    page = 1,
    pageSize = 10,
  ): Promise<{ items: ScrimHistoryItem[]; total: number }> => {
    const { data } = await api.get<{ items: ScrimHistoryItem[]; total: number }>(
      `/rosters/${encodeURIComponent(teamId)}/scrims`,
      { params: { page, pageSize } },
    );
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number(data?.total ?? 0),
    };
  },

  // --- Заявки игроков на вступление в команду (инверсия приглашений) ---

  /** Игрок подаёт заявку в команду на выбранную роль. */
  submitApplication: async (
    teamId: string,
    role: RosterMemberRole,
  ): Promise<{ success: boolean; id?: string }> => {
    const { data } = await api.post<{ success: boolean; id?: string }>(
      `/rosters/${encodeURIComponent(teamId)}/applications`,
      { role },
    );
    return data ?? { success: false };
  },

  /** Заявки команды (для капитана). */
  getTeamApplications: async (teamId: string): Promise<RosterApplication[]> => {
    const { data } = await api.get<RosterApplication[]>(
      `/rosters/${encodeURIComponent(teamId)}/applications`,
    );
    return Array.isArray(data) ? data : [];
  },

  /** Заявки текущего пользователя. */
  getMyApplications: async (): Promise<RosterApplication[]> => {
    const { data } = await api.get<RosterApplication[]>(
      `/rosters/user/applications`,
    );
    return Array.isArray(data) ? data : [];
  },

  /** Принять заявку (капитан). */
  acceptApplication: async (applicationId: string): Promise<void> => {
    await api.post(
      `/rosters/applications/${encodeURIComponent(applicationId)}/accept`,
    );
  },

  /** Отклонить заявку (капитан). */
  rejectApplication: async (applicationId: string): Promise<void> => {
    await api.post(
      `/rosters/applications/${encodeURIComponent(applicationId)}/reject`,
    );
  },

  /** Отозвать свою заявку (заявитель). */
  cancelApplication: async (applicationId: string): Promise<void> => {
    await api.delete(
      `/rosters/applications/${encodeURIComponent(applicationId)}`,
    );
  },
};

/** Роль в заявке/приглашении (LoL). */
export type RosterMemberRole =
  | "Top"
  | "Jungle"
  | "Mid"
  | "Adc"
  | "Support"
  | "Substitute"
  | "Coach";

/** Заявка игрока на вступление в команду. */
export type RosterApplication = {
  id: string;
  teamId: string;
  teamName?: string | null;
  applicantUserId: string;
  applicantName?: string | null;
  applicantAvatarUrl?: string | null;
  applicantRank?: string | null;
  role: RosterMemberRole;
  status: "Pending" | "Accepted" | "Rejected" | "Cancelled";
  createdAt?: string;
  respondedAt?: string | null;
};

// Lightweight public roster list item (D1)
export type RosterListItem = {
  id: string;
  name: string;
  tag: string;
  icon?: string | null;
  logoUrl?: string | null;
  /** Баннер/обложка команды для шапки карточки (если задан). */
  banner?: string | null;
  memberCount: number;
  /** Максимум участников в команде (включая запас/тренера). */
  maxMembers?: number;
  isLookingForPlayers: boolean;
  isVerified: boolean;
  createdAt: string;
  /** Средний ранг команды (тир+дивизион, напр. "PLATINUM II"); null если нет рангов. */
  averageRank?: string | null;
  /** Превью участников (до 5) для стека аватарок и подсветки занятых позиций. */
  members?: Array<{
    avatarUrl?: string | null;
    displayName?: string | null;
    /** Командная роль (Top/Jungle/Mid/Adc/Support/Substitute/Coach). */
    role?: string | null;
  }>;
};

export type RosterListResponse = {
  items: RosterListItem[];
  total: number;
  page: number;
  pageSize: number;
};

export type ScrimHistoryItem = {
  id: string;
  team1Id: string;
  team2Id?: string | null;
  status: string;
  scheduledAt?: string | null;
  createdAt: string;
  completedAt?: string | null;
  winnerId?: string | null;
  team1Score: number;
  team2Score: number;
  team1?: { id: string; name: string; tag: string; slug?: string | null } | null;
  team2?: { id: string; name: string; tag: string; slug?: string | null } | null;
};

export type ScrimScheduleSlot = {
  dayOfWeek: number;
  startMinutes: number;
  endMinutes: number;
};
